<template>
  <div ref="chartContainer" :style="style"></div>
</template>

<script setup>
import { ref, watch, onMounted, onBeforeUnmount } from "vue"
import * as echarts from "echarts"
import { BarChart, LineChart, PieChart } from "echarts/charts"
import {
  TooltipComponent,
  LegendComponent,
  GridComponent
} from "echarts/components"
import { LabelLayout, UniversalTransition } from "echarts/features"
import { CanvasRenderer } from "echarts/renderers"

echarts.use([
  BarChart,
  LineChart,
  PieChart,
  TooltipComponent,
  LegendComponent,
  GridComponent,
  LabelLayout,
  UniversalTransition,
  CanvasRenderer
])

const props = defineProps({
  options: { type: Object || null, default: null },
  style: {
    type: Object,
    default: () => ({ width: "100%", height: "100%" })
  },
  loading: { type: Boolean, default: false }
})

const chartContainer = ref(null)
let chartInstance = null
let resizeObserver = null

const defaultOptions = {
  graphic: [
    {
      type: "text",
      left: "center",
      top: "middle",
      style: {
        text: "No Data Available",
        fontSize: 18,
        fontWeight: "bold",
        fill: "#999"
      }
    }
  ]
}

const initChart = () => {
  if (chartContainer.value) {
    chartInstance = echarts.init(chartContainer.value)
    chartInstance.setOption(props.options || defaultOptions)
    props.loading ? chartInstance.showLoading() : chartInstance.hideLoading()
  }
}

const resizeChart = () => {
  if (chartInstance) {
    chartInstance.resize()
  }
}

const initResizeObserver = () => {
  resizeObserver = new ResizeObserver(() => resizeChart())

  if (chartContainer.value) {
    resizeObserver.observe(chartContainer.value)
  }
}

onMounted(() => {
  initChart()
  initResizeObserver()
})

onBeforeUnmount(() => {
  if (chartInstance) {
    chartInstance.dispose()
  }

  if (resizeObserver) {
    resizeObserver.unobserve(chartContainer.value)
    resizeObserver.disconnect()
  }
})

watch(
  () => props.options,
  (newOptions) => {
    if (chartInstance) {
      chartInstance.setOption(newOptions, true)
    }
  },
  { deep: true }
)

watch(
  () => props.loading,
  (loading) => {
    if (chartInstance) {
      loading ? chartInstance.showLoading() : chartInstance.hideLoading()
    }
  }
)
</script>
